import { useAuth } from "../context/AuthContext";
import { useEmbedToken } from "../context/EmbedTokenContext";

// Single source of truth for "what is the current user's role / are they an
// admin?" — works correctly on BOTH the normal dashboard surface and the
// partner-SSO embed iframe.
//
// Why this exists (the bug it fixes):
// Several places resolved the role as
//     currentUser?.role || embedClaims?.role
// which prefers the localStorage `useAuth` user. Inside an embed iframe the
// browser may still hold a DIFFERENT user's localStorage session (e.g. an
// admin who used the dashboard in the same browser), so a NON-admin embed
// user would inherit the stale admin role and wrongly see admin-only UI
// (Users column, Reassign, "All users" filter, etc.).
//
// Correct precedence: when an embed JWT is present, the IFRAME's identity is
// defined by that token — it must win over any localStorage user. Outside
// the embed surface, the auth user is authoritative.
//
// Use `useEffectiveRole()` / `useIsAdmin()` everywhere instead of hand-rolling
// the `currentUser?.role || embedClaims?.role` expression.

export const ADMIN_ROLES = ["tenant_admin", "super_admin", "partner_admin"];

export const isAdminRole = (role) => ADMIN_ROLES.includes(role);

// Resolve the effective role string (or null). Embed JWT claims take
// precedence whenever they exist (we're in the iframe); otherwise the
// localStorage auth user.
export const useEffectiveRole = () => {
  const { user: currentUser } = useAuth();
  const { claims: embedClaims } = useEmbedToken();
  // embedClaims present === embed surface → the token defines identity.
  if (embedClaims?.role) return embedClaims.role;
  return currentUser?.role || null;
};

// Effective user id with the same embed-first precedence.
export const useEffectiveUserId = () => {
  const { user: currentUser } = useAuth();
  const { claims: embedClaims } = useEmbedToken();
  if (embedClaims) return embedClaims.sub || null;
  return currentUser?.user_id || null;
};

// Convenience: is the effective user an admin?
export const useIsAdmin = () => isAdminRole(useEffectiveRole());

export default useEffectiveRole;
